/**
 * Hook for generating single videos
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type {
  GenerateVideoRequest,
  VideoGenerationResult,
  VideoStyle,
} from "@/features/studio-ai/types/studio-ai.types"

import { generateVideo } from "@/api/studio-ai-api"
import i18n from "@/languageConfig"
import { getApiErrorMessage } from "@/lib/api-error-message"
import { queryKeys } from "@/lib/query-keys"
import { useStudioAIStore } from "@/stores/studio-ai-store"

export function useGenerateVideo() {
  const queryClient = useQueryClient()
  const updateGenerationState = useStudioAIStore((state) => state.updateGenerationState)
  const addVideo = useStudioAIStore((state) => state.addVideo)

  return useMutation({
    mutationFn: (request: GenerateVideoRequest) => generateVideo(request),
    onMutate: () => {
      updateGenerationState({
        isGenerating: true,
        progress: 0,
        status: i18n.t("studioAi.generationForm.startingVideo"),
      })
    },
    onSuccess: (result: VideoGenerationResult) => {
      updateGenerationState({
        currentJobId: result.job_id,
        status: i18n.t("studioAi.common.processing"),
      })

      // Add to videos list
      addVideo({
        id: result.job_id,
        job_id: result.job_id,
        prompt: result.prompt,
        original_prompt: result.original_prompt,
        status: result.status,
        duration: result.duration,
        aspect_ratio: result.aspect_ratio,
        quality: result.quality,
        style: result.style as VideoStyle,
        video_url: result.video_url,
        thumbnail_url: result.thumbnail_url,
        error_message: result.error_message,
        created_at: result.created_at,
        completed_at: result.completed_at,
        metadata: result.metadata,
      })

      toast.success(i18n.t("studioAi.generationForm.videoGenerationStarted"), {
        description: result.job_id
          ? `${i18n.t("studioAi.progress.jobId")} ${result.job_id}`
          : undefined,
      })

      // Invalidate queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.sora2.jobs() })
    },
    onError: (error: unknown) => {
      updateGenerationState({
        isGenerating: false,
        status: i18n.t("studioAi.common.failed"),
      })

      // The backend puts the useful text in the JSON body (`{"error": ...}`),
      // e.g. the Azure 410 for a retired Sora model version; Axios' own
      // message is just "Request failed with status code 500".
      const message = getApiErrorMessage(error, i18n.t("studioAi.common.anErrorOccurred"))
      // A deleted or unshared style template gets its own message from the
      // wizard (the same split as useGenerateImage), so don't toast it twice.
      if (message.toLowerCase().includes("style template not found or access denied")) {
        return
      }
      toast.error(i18n.t("studioAi.generationForm.failedToGenerateVideo"), {
        description: message,
      })
    },
  })
}
